import { Children, forwardRef, isValidElement, memo, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { SelectProps as MuiSelectProps } from '@mui/material';
import { Box, FormHelperText, InputAdornment, MenuItem } from '@mui/material';
import { StyledLabel, StyledSelect, Wrapper } from './Select.styles';
import { ExpandMore } from '@mui/icons-material';
import type { FormItemUiDefinition } from '../../../types/formItem.ts';
import { getUIDefinitionIconStyles, getUiDefinitionContainerStyles, getUiDefinitionTextStyles } from '../../../utils/styles.ts';
import { getIcon } from 'utils/common.ts';

const getSelectedItemLabel = (children: ReactNode, selected: unknown): ReactNode => {
  for (const child of Children.toArray(children)) {
    if (isValidElement<{ value?: unknown; children?: ReactNode }>(child)) {
      const { value, children: label } = child.props;
      if (value === selected) {
        return label;
      }
    }
  }
  return typeof selected === 'string' || typeof selected === 'number' ? selected : '';
};

export type SelectProps = MuiSelectProps & {
  helperText?: string;
  placeholder?: string;
  className?: string;
  hideDefaultItem?: boolean;
  disableHelperText?: boolean;
  background?: 'transparent' | 'white';
  lightError?: boolean;
  formItemUiDefinition?: FormItemUiDefinition;
  renderValueOverride?: () => JSX.Element;
  ignoreIcon?: boolean;
  dataTestId?: string;
};

export const Select = memo(
  forwardRef<HTMLSelectElement, SelectProps>(
    (
      {
        value,
        multiple,
        placeholder,
        className,
        displayEmpty,
        hideDefaultItem = false,
        label,
        helperText,
        disableHelperText = false,
        error,
        lightError,
        children,
        formItemUiDefinition,
        renderValueOverride: renderItemOverride,
        ignoreIcon = false,
        dataTestId,
        sx,
        ...rest
      },
      ref
    ) => {
      const { t } = useTranslation();
      const styles = getUiDefinitionTextStyles(formItemUiDefinition);
      const { border: _containerBorder, ...containerBackgroundStyles } = getUiDefinitionContainerStyles(formItemUiDefinition) as CSSProperties;
      const Icon = !ignoreIcon && formItemUiDefinition?.icon ? getIcon(formItemUiDefinition?.icon ?? '') : null;
      const valueLabelSx = {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        minWidth: 0,
        ml: 0.75,
        ...(styles.fontStyle === 'italic' ? { paddingRight: '0.2em' } : {}),
        ...styles,
      };

      const renderValueWithIcon =
        !multiple && Icon
          ? (selected: unknown) => {
              const isEmpty = selected === '' || selected === undefined || selected === null;
              return (
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', maxWidth: '100%', minWidth: 0 }}>
                  <Icon sx={{ ...getUIDefinitionIconStyles(formItemUiDefinition), flexShrink: 0 }} />
                  <Box component="span" sx={valueLabelSx}>
                    {isEmpty ? (hideDefaultItem ? '' : (placeholder ?? t('common.select'))) : getSelectedItemLabel(children, selected)}
                  </Box>
                </Box>
              );
            }
          : undefined;

      return (
        <Wrapper>
          <StyledLabel>{label}</StyledLabel>
          <StyledSelect
            ref={ref}
            data-testid={dataTestId}
            sx={[{ ...styles, ...containerBackgroundStyles }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
            MenuProps={{ disableScrollLock: true }}
            className={className}
            value={multiple ? value : (value?.toString() ?? '')}
            multiple={multiple}
            IconComponent={ExpandMore}
            displayEmpty={displayEmpty}
            error={error}
            lightError={lightError}
            formItemUiDefinition={formItemUiDefinition}
            slotProps={{
              input: {
                sx: { ...styles },
              },
            }}
            startAdornment={
              Icon && multiple ? (
                <InputAdornment position="start">
                  <Icon sx={getUIDefinitionIconStyles(formItemUiDefinition)} />
                </InputAdornment>
              ) : undefined
            }
            renderValue={renderItemOverride ?? renderValueWithIcon}
            {...rest}>
            {!hideDefaultItem && <MenuItem value="">{placeholder ?? t('common.select')}</MenuItem>}
            {children}
          </StyledSelect>
          {!disableHelperText && helperText && (
            <FormHelperText error={error} variant="filled" title={helperText}>
              {helperText}
            </FormHelperText>
          )}
        </Wrapper>
      );
    }
  )
);
